import React, { useState, useEffect, useCallback } from 'react';

interface CampaignLink {
    linkId: string;
    label: string;
    createdBy: string;
    createdAt: string;
    usageCount: number;
    active: boolean;
    signupUrl: string;
}

interface Props {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
}

export const CampaignLinksSection: React.FC<Props> = ({ API_BASE, authHeaders }) => {
    const [links, setLinks] = useState<CampaignLink[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [newLabel, setNewLabel] = useState('');
    const [creating, setCreating] = useState(false);
    const [createStatus, setCreateStatus] = useState<string | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

    const fetchLinks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/admin/campaign-links`, {
                method: 'GET',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Failed to load links');
            const data = await res.json();
            setLinks(data.links ?? []);
        } catch (err: any) {
            setError(err.message || 'Failed to load campaign links');
        } finally {
            setLoading(false);
        }
    }, [API_BASE, authHeaders]);

    useEffect(() => { fetchLinks(); }, [fetchLinks]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newLabel.trim()) return;
        setCreating(true);
        setCreateStatus(null);
        try {
            const res = await fetch(`${API_BASE}/admin/campaign-links`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ label: newLabel.trim() }),
            });
            if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Failed to create link');
            setNewLabel('');
            setCreateStatus('Link created successfully');
            await fetchLinks();
        } catch (err: any) {
            setCreateStatus(err.message || 'Failed to create link');
        } finally {
            setCreating(false);
        }
    };

    const handleToggleActive = async (link: CampaignLink) => {
        try {
            const res = await fetch(`${API_BASE}/admin/campaign-links/${link.linkId}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({ active: !link.active }),
            });
            if (!res.ok) throw new Error('Failed to update link');
            await fetchLinks();
        } catch (err: any) {
            setError(err.message || 'Failed to update link');
        }
    };

    const handleDelete = async (linkId: string) => {
        try {
            const res = await fetch(`${API_BASE}/admin/campaign-links/${linkId}`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error('Failed to delete link');
            setDeleteConfirm(null);
            await fetchLinks();
        } catch (err: any) {
            setError(err.message || 'Failed to delete link');
        }
    };

    const handleCopy = (link: CampaignLink) => {
        navigator.clipboard.writeText(link.signupUrl).then(() => {
            setCopiedId(link.linkId);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    return (
        <div>
            <h2>Campaign Invite Links</h2>
            <p style={{ color: '#555', marginTop: 0 }}>
                Reusable signup links you can include in email campaigns. Each link tracks how many accounts were created with it.
            </p>

            <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                <input
                    type="text"
                    className="dashboard-input"
                    placeholder="Link label (e.g. June Newsletter)"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    style={{ flexGrow: 1, minWidth: 200 }}
                />
                <button type="submit" disabled={creating || !newLabel.trim()} className="btn btn-primary">
                    {creating ? 'Creating…' : 'Create Link'}
                </button>
                <button type="button" onClick={fetchLinks} className="btn btn-secondary" style={{ marginLeft: 4 }}>
                    Refresh
                </button>
            </form>
            {createStatus && <p className="status">{createStatus}</p>}

            {loading && <p className="status">Loading…</p>}
            {error && <p className="status status-error">{error}</p>}

            {!loading && links.length === 0 && (
                <p className="status">No campaign links yet. Create one above.</p>
            )}

            {links.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                        <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                            <th style={thStyle}>Label</th>
                            <th style={thStyle}>Signup URL</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Uses</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {links.map((link) => (
                            <tr key={link.linkId} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={tdStyle}>
                                    <strong>{link.label}</strong>
                                    <br />
                                    <span style={{ color: '#999', fontSize: 11 }}>
                                        {new Date(link.createdAt).toLocaleDateString()} · id: {link.linkId}
                                    </span>
                                </td>
                                <td style={tdStyle}>
                                    <code style={{ fontSize: 11, wordBreak: 'break-all', color: '#667eea' }}>
                                        {link.signupUrl}
                                    </code>
                                </td>
                                <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
                                    {link.usageCount ?? 0}
                                </td>
                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '2px 10px',
                                        borderRadius: 12,
                                        fontSize: 12,
                                        background: link.active ? '#dcfce7' : '#fee2e2',
                                        color: link.active ? '#15803d' : '#b91c1c',
                                        fontWeight: 600,
                                    }}>
                                        {link.active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                    <button
                                        className="btn btn-secondary"
                                        style={{ fontSize: 12, padding: '4px 10px', marginRight: 4 }}
                                        onClick={() => handleCopy(link)}
                                    >
                                        {copiedId === link.linkId ? '✓ Copied!' : 'Copy URL'}
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        style={{ fontSize: 12, padding: '4px 10px', marginRight: 4 }}
                                        onClick={() => handleToggleActive(link)}
                                    >
                                        {link.active ? 'Deactivate' : 'Activate'}
                                    </button>
                                    {deleteConfirm === link.linkId ? (
                                        <>
                                            <button
                                                className="btn btn-danger"
                                                style={{ fontSize: 12, padding: '4px 10px', marginRight: 4 }}
                                                onClick={() => handleDelete(link.linkId)}
                                            >
                                                Confirm Delete
                                            </button>
                                            <button
                                                className="btn btn-secondary"
                                                style={{ fontSize: 12, padding: '4px 10px' }}
                                                onClick={() => setDeleteConfirm(null)}
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            className="btn btn-danger"
                                            style={{ fontSize: 12, padding: '4px 10px' }}
                                            onClick={() => setDeleteConfirm(link.linkId)}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

const thStyle: React.CSSProperties = {
    padding: '8px 12px',
    fontWeight: 600,
    borderBottom: '2px solid #ddd',
};

const tdStyle: React.CSSProperties = {
    padding: '10px 12px',
    verticalAlign: 'middle',
};
